import { useEffect, useRef, useState } from 'react'

import { faSliders } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import {
  useAccessToken,
  sendGTMEvent,
} from '@b-stock/fe-core/bstock-next/components'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  Button,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'

import SavedSearchButton from '@/components/listings/search/savedSearch/SavedSearchButton'
import { GTM_PLATFORM } from '@/helpers/telemetry/constants'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import type { ListingSearchContextValue } from '../ListingSearchProvider'
import FilterContainer, { FilterList } from './shared/FilterContainer'
import type { ListingFilter, ListingFilterSet } from './types'

type PendingFilterEvent = {
  filter_type: string
  filter_value?: string
  filter_source: string
}

// Range filters default to `[null, null]`, categoricals to `[]`, and
// distance-range / search-query to objects or `null` — so a non-zero key
// count alone is not enough to call a filter "active". Treat any filled-in
// element / property / primitive as the signal.
const hasActiveValue = ({ value }: { value: unknown }): boolean => {
  if (value == null) return false
  if (Array.isArray(value)) return value.some((v) => v != null)
  if (value instanceof Set || value instanceof Map) return value.size > 0
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(
      (v) => v != null
    )
  }
  return true
}

type SearchFiltersProps<T> = {
  className?: string
  filters: ListingFilter<T>[]
  hiddenFilters?: ListingFilterSet
  searchCtx: ListingSearchContextValue
  excludeSavedSearch?: boolean
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 100%;
  position: relative;
`

const SearchFilters = <T,>({
  className,
  filters,
  hiddenFilters,
  searchCtx,
  excludeSavedSearch,
}: SearchFiltersProps<T>) => {
  const accessToken = useAccessToken()
  const isLoggedIn = !!accessToken
  const { isLoading } = searchCtx
  const { trackButtonClicked } = useAnalytics()
  const pendingFilterRef = useRef<PendingFilterEvent | null>(null)
  const prevDataRef = useRef(searchCtx.data)

  // Auto-expand when any hidden-section filter already has an active
  // value (e.g. from a URL parameter or saved search) so the user can
  // see and edit it without clicking the toggle first.
  const [isSeeAllExpanded, setIsSeeAllExpanded] = useState(() => {
    if (!hiddenFilters) return false
    return filters.some(
      (f) => hiddenFilters.has(f) && hasActiveValue(f.getOwnProps(searchCtx))
    )
  })

  const toggleSeeAll = () => {
    const next = !isSeeAllExpanded
    setIsSeeAllExpanded(next)
    trackButtonClicked({
      buttonName: 'filter_see_all_toggle',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      expanded: next,
    })
  }

  useEffect(() => {
    const dataIsNew = searchCtx.data !== prevDataRef.current
    prevDataRef.current = searchCtx.data

    if (!pendingFilterRef.current || !searchCtx.data || !dataIsNew) return

    const { filter_type, filter_value, filter_source } =
      pendingFilterRef.current
    pendingFilterRef.current = null

    const activeFilters = Object.keys(searchCtx.state.filters).filter((k) => {
      const val =
        searchCtx.state.filters[k as keyof typeof searchCtx.state.filters]
      if (val == null) return false
      if (Array.isArray(val)) return val.length > 0
      return true
    })

    sendGTMEvent({
      event: 'filter_apply',
      platform: GTM_PLATFORM,
      filter_type,
      filter_value,
      filter_count: searchCtx.getActiveFilterCount(),
      filter_facets_used: activeFilters.join(','),
      results_count: searchCtx.data.total,
      filter_source,
    })
  }, [
    searchCtx,
    searchCtx.data,
    searchCtx.state.filters,
    searchCtx.getActiveFilterCount,
  ])

  const visibleList = hiddenFilters
    ? filters.filter((f) => !hiddenFilters.has(f))
    : filters
  const hiddenList = hiddenFilters
    ? filters.filter((f) => hiddenFilters.has(f))
    : []

  const renderFilter = (FilterComp: ListingFilter<T>) => {
    const filterProps = FilterComp.getOwnProps(searchCtx)

    const shouldDisplayFilter =
      FilterComp.isAlwaysDisplayed ||
      Object.keys(filterProps.value || {}).length ||
      filterProps.value === null || // it is empty range value
      Object.keys(filterProps.items || {}).length

    const showForCurrentUser = filterProps.forLoggedUsersOnly
      ? isLoggedIn
      : true
    const propsOnChange = filterProps.onChange
    filterProps.onChange = (value) => {
      propsOnChange(value)

      pendingFilterRef.current = {
        filter_type: FilterComp.label,
        filter_value: typeof value === 'string' ? value : undefined,
        filter_source: 'sidebar',
      }
    }

    // Create onExpand callback for tracking filter title clicks
    const handleExpand =
      FilterComp.onExpand ||
      (FilterComp.getOnExpand
        ? FilterComp.getOnExpand(trackButtonClicked)
        : undefined)

    return shouldDisplayFilter &&
      showForCurrentUser &&
      FilterComp.label !== 'Filters.searchQuery' ? (
      <FilterContainer
        key={FilterComp.label}
        expandedOnInit={FilterComp.expandedOnInit}
        inSidebar
        label={FilterComp.label}
        onExpand={handleExpand}
        shouldUseExpandMemory
        tooltip={FilterComp.tooltip}
      >
        <FilterComp {...filterProps} desktopScreen />
      </FilterContainer>
    ) : null
  }

  return (
    !isLoading && (
      <Container className={className} data-testid="SearchFilters">
        {excludeSavedSearch ? null : <SavedSearchButton />}

        <FilterList>
          {visibleList.map(renderFilter)}
          {isSeeAllExpanded && hiddenList.map(renderFilter)}
          {hiddenList.length > 0 && (
            <Button
              type="button"
              appearance={'secondary'}
              onClick={toggleSeeAll}
              aria-expanded={isSeeAllExpanded}
              data-testid="see-all-filters-toggle"
            >
              <FontAwesomeIcon icon={faSliders} />
              <FormattedMessage
                id={
                  isSeeAllExpanded
                    ? 'Filters.seeAllFilters.hide'
                    : 'Filters.seeAllFilters.show'
                }
              />
            </Button>
          )}
        </FilterList>
      </Container>
    )
  )
}

export default SearchFilters
